import React, {useEffect, useMemo, useRef, useState} from 'react';
import UserInfo from '../../Components/User/UserInfo';
import TopTitleBlock from '../../Components/TopTitleBlock/TopTitleBlock';
import {MyTeamUser, ValuesRequestTeamType} from '../../types/TypesPage';
import UserTeamHeader from '../../Components/User/UserTeamHeader/UserTeamHeader';
import FilterTeamHeader from './Components/FilterTeamHeader';
import MyTeamFilterNew from '../../Components/Filter/MyTeamFilterNew';
import MyTeamListElement from './Components/MyTeamListElement';
import InfoColorPanel, {
	QuickFilterKey,
} from './Components/InfoColorPanel/InfoColorPanel';
import ClientInfoPopup from './Components/ClientInfoPopup/ClientInfoPopup';
import s from '../CashOrders/CashOrdersList/CashOrdersList.module.css';
import LightGreenButton from '../../Components/Buttons/LightGreenButton';
import {useTranslation} from 'react-i18next';
import {useGetMyTeamQuery} from '../../redux/userClientsApi';
import PreLoaderComponent from '../../assets/Animations/PreLoaderComponent/PreLoaderComponent';
import {useLocation, useNavigate} from 'react-router-dom';
import {ExcelExportButton} from '../../Components/ExelPage/ExcelExportButton';
import {dateFormatter} from '../../assets/utils/dateUtils';
import {useStatusRestriction} from '../../hooks/useStatusRestriction';
import StatusRestrictionMessage from '../../Components/StatusRestrictionMessage/StatusRestrictionMessage';

export type FilterModeType = 'manager_type' | 'optima_type' | 'all';

type QuickFilterMap = Record<QuickFilterKey, keyof ValuesRequestTeamType>;

const quickFilterMap: QuickFilterMap = {
	not_active_contract: 'not_active_contract',
	manager_type: 'manager_type',
	old_contract: 'old_contract',
	old_tree: 'old_tree',
};

const MyTeam: React.FC = () => {
	const {t} = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const restrictions = useStatusRestriction();

	const month = dateFormatter(new Date(new Date().setDate(1)));

	const defaultFilterValues: ValuesRequestTeamType = {
		non_filter: true,
		month,
	};

	const [showAll, setShowAll] = useState(false);
	const [filters, setFilters] =
		useState<ValuesRequestTeamType>(defaultFilterValues);
	const [clientInfo, setClientInfo] = useState<number>(0);
	
	const handleSetClientInfo = (id: number) => {
		setClientInfo(id);
	};

	const activeQuickFilter = useMemo<QuickFilterKey | null>(() => {
		if (filters.not_active_contract) return 'not_active_contract';
		if (filters.manager_type) return 'manager_type';
		if (filters.old_contract) return 'old_contract';
		if (filters.old_tree) return 'old_tree';
		return null;
	}, [
		filters.manager_type,
		filters.not_active_contract,
		filters.old_contract,
		filters.old_tree,
	]);

	const handleQuickFilterSelect = (key: QuickFilterKey) => {
		setFilters((prev) => {
			const isActive = Boolean(prev?.[quickFilterMap[key]]);
			const currentMonth = prev?.month ?? month;

			const cleaned: ValuesRequestTeamType = {...(prev || {})};

			(
				Object.values(quickFilterMap) as (keyof ValuesRequestTeamType)[]
			).forEach((clearKey) => {
				delete cleaned[clearKey];
			});
			delete cleaned.loyal_type;
			delete cleaned.not_active;
			delete cleaned.registration_date;
			delete cleaned.active_points_from;

			if (isActive) {
				return {
					...cleaned,
					month: currentMonth,
					non_filter: true,
				};
			}

			return {
				...cleaned,
				month: currentMonth,
				non_filter: false,
				[quickFilterMap[key]]: true,
			};
		});
		setShowAll(false);
	};
	const handleClickMoreButton = () => {
		setShowAll(true);
	};
	const applyFilters = setFilters;
	
	const tableRef = useRef<HTMLDivElement>(null);
	const shouldScrollRef = useRef(false);
	
	const scrollToTable = () => {
		const isMobile = window.innerWidth <= 850.98;
		if (isMobile) {
			shouldScrollRef.current = true;
		}
	};

	const {data: myTeam, isLoading} = useGetMyTeamQuery(filters, {
		skip: !restrictions.canAccessMyTeamPage,
	});

	useEffect(() => {
		if (shouldScrollRef.current && !isLoading && tableRef.current && myTeam) {
			const timeoutId = setTimeout(() => {
				tableRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				});
				shouldScrollRef.current = false;
			}, 300);
			return () => clearTimeout(timeoutId);
		}
	}, [isLoading, myTeam, filters]);

	const forRenderList = useMemo(() => {
		return showAll ? myTeam?.users : myTeam?.users?.slice(0, 49);
	}, [myTeam?.users, showAll]);

	useEffect(() => {
		if (location.state?.defaultFilters) {
			setFilters({
				...defaultFilterValues,
				...location.state.defaultFilters,
			});
			// чистим state после первого рендера
			setTimeout(() => {
				navigate(location.pathname, {replace: true});
			}, 0);
		} else {
			setFilters(defaultFilterValues);
		}
		
		// Проверяем, нужно ли открыть ClientInfoPopup
		if (location.state?.openClientInfo) {
			const userId = Number(location.state.openClientInfo);
			if (userId) {
				setClientInfo(userId);
			}
			// Очищаем state после использования
			setTimeout(() => {
				navigate(location.pathname, {replace: true});
			}, 0);
		}
		
		setShowAll(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!restrictions.canAccessMyTeamPage) {
		return (
			<div className="page__content">
				<StatusRestrictionMessage message={t('Сторінка недоступна')} />
			</div>
		);
	}

	return (
		<div className="page__content">
			<TopTitleBlock title={t('Моя команда')} />
			<div className="page__user user">
				<div className="user__row">
					<div className="user__column">
						<UserInfo />
					</div>
					<div className="user__column">
						<UserTeamHeader setFilters={applyFilters} month={month} onFilterChange={scrollToTable} />
					</div>
				</div>
			</div>
			<InfoColorPanel
				activeKey={activeQuickFilter}
				onSelect={handleQuickFilterSelect}
			/>
			{myTeam && myTeam.users && (
				<div className={s.importButton}>
					<ExcelExportButton data={myTeam.users} />
				</div>
			)}
			<div className="page__filter-team filter-team">
				<MyTeamFilterNew setFilters={applyFilters} />
				{isLoading ? (
					<PreLoaderComponent type={'gallery'} size={'big'} />
				) : (
					<>
						<div className="filter-team__body" ref={tableRef}>
							<FilterTeamHeader />
							{forRenderList?.map((user: MyTeamUser, index: number) => (
								<MyTeamListElement
									key={user.user_id}
									index={index}
									user={user}
									showAll={showAll}
									selectedMonth={month}
								/>
							))}
						</div>
						{!showAll && myTeam && Boolean(myTeam.users.length > 49) && (
							<div className="filter-team__footer">
								<div className={s.buttonWrapper}>
									<LightGreenButton
										type={'button'}
										text={t('дивитися більше')}
										onClick={handleClickMoreButton}
									/>
								</div>
							</div>
						)}
					</>
				)}
			</div>
			{clientInfo !== 0 && (
				<ClientInfoPopup
					clientInfo={clientInfo}
					setClientInfo={handleSetClientInfo}
					selectedMonth={month}
				/>
			)}
		</div>
	);
};

export default MyTeam;
